import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { ManualLoader, Wrapper } from "../../../components";
import { WizardShell } from "../../../components/Layouts/WizardShell";
import { StepNavigation } from "../../Account/Register/StepNavigation";
import { RegisterReviewContainer } from "../../Account/Register/Register.style";
import {
  AuthorizationToAssist,
  AuthorizationToAssistData,
} from "./steps/AuthorizationToAssist";
import { MemberProfiles } from "./steps/MemberProfiles";
import { RentalDetails } from "./steps/RentalDetails";
import { StudentDetails } from "./steps/StudentDetails";
import { SpecialNeeds } from "./steps/SpecialNeeds";
import { SpecialStatus } from "./steps/SpecialStatus";
import { HouseholdIncome } from "./steps/HouseholdIncome";
import { Assets } from "./steps/Assets";
import { FinancialSummary } from "./steps/FinancialSummary";
import { UploadCenter } from "./steps/UploadCenter";
import { ReviewSign } from "./steps/ReviewSign";
import {
  ApplicationData,
  ApplicationRecord,
  STEP_ORDER,
  computeResumePoint,
} from "./applicationState";
import { useUnsavedAffordableStepNavigation } from "./stepNavigationGuard";
import {
  useGetApplicationsMutation,
  useGetUserQuery,
  useSaveApplicationDataMutation,
  useSubmitAffordableApplicationMutation,
} from "../../../store/services/privateApi";
import {
  useGetIncomeLimitsQuery,
  useGetUnitQuery,
} from "../../../store/services/publicApi";
import { securityOptions } from "../../../shared/config/storeConfig";
import { parseMoveInTooSoonError } from "../../../helpers/moveInTooSoon";
import type { MoveInTooSoonInfo } from "../../../helpers/moveInTooSoon";
import { MoveInDateCorrection } from "../../Account/Register/MoveInDateCorrection";

interface PropertyContact {
  phone?: string;
  email?: string;
  name?: string;
  // Per-property: when true, the Upload Center doesn't block submission.
  affordableDocumentUploadOptional?: boolean;
}

export const AffordableApplication = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get("applicationId") || "";
  const propertyId = searchParams.get("propertyId") || "";
  // Unit & lease context for the Review & Sign summary. Direct-unit mode carries
  // these through the apply URL; holding mode has no unit in the URL, so they stay
  // empty and Review & Sign omits the Unit & Lease block.
  const unitID = searchParams.get("unitID") || "";
  const moveInDateParam = searchParams.get("moveInDate") || "";
  const leaseTermParam = searchParams.get("leaseTerm") || "";

  const steps = [
    t("affordable.application.steps.authorizationToAssist"),
    t("affordable.application.steps.memberProfiles"),
    t("affordable.application.steps.rentalDetails"),
    t("affordable.application.steps.studentStatus"),
    t("affordable.application.steps.specialNeeds"),
    t("affordable.application.steps.specialStatus"),
    t("affordable.application.steps.householdIncome"),
    t("affordable.application.steps.assets"),
    t("affordable.application.steps.financialSummary"),
    t("affordable.application.steps.uploadCenter"),
    t("affordable.application.steps.reviewSign"),
  ];

  const [getApplications, { isLoading: isFetching }] =
    useGetApplicationsMutation();
  const [saveApplicationData, { isLoading: isSaving }] =
    useSaveApplicationDataMutation();
  const [submitAffordable, { isLoading: isSubmitting }] =
    useSubmitAffordableApplicationMutation();
  const { data: userData, isLoading: isUserLoading } = useGetUserQuery(
    null
  ) as {
    data?: {
      user?: {
        email?: string;
        person?: { FirstName?: string; LastName?: string };
      };
    };
    isLoading: boolean;
  };

  // Property contact info (phone/email) — used by step alerts that ask the
  // applicant to contact the office.
  const { data: propertyData } = useGetIncomeLimitsQuery(propertyId, {
    skip: !propertyId,
  }) as { data: { property?: PropertyContact } | undefined };
  const propertyContact = propertyData?.property;

  const [hasHydrated, setHasHydrated] = useState(false);
  const [applicationData, setApplicationData] = useState<ApplicationData>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [moveInTooSoon, setMoveInTooSoon] = useState<MoveInTooSoonInfo | null>(
    null
  );
  const [selectedMoveInDate, setSelectedMoveInDate] = useState(moveInDateParam);
  const lastReviewPayloadRef = useRef<unknown>(null);
  // A reopened (pending_resubmit) application shows a corrections banner and keeps the
  // wizard editable; only a 'submitted' app renders the read-only success screen.
  const [isResubmit, setIsResubmit] = useState(false);
  const unsavedStepNavigationMessage = t(
    "affordable.application.unsavedStepNavigation"
  ) as string;
  const { navigateBack: handleBack, navigateToStep: handleStepClick } =
    useUnsavedAffordableStepNavigation({
      currentStep,
      completedSteps,
      setCurrentStep,
      setCompletedSteps,
      confirmMessage: unsavedStepNavigationMessage,
    });

  // Rent is not part of applicationData, so it comes from the same public unit
  // endpoint the Unit Details panel uses. Deferred to the Review & Sign step (and
  // skipped without a unit in context) so the rest of the wizard doesn't pay for a
  // lookup it never renders.
  const reviewStep = STEP_ORDER.length - 1;
  // Also fetched at the rental-details step so its pet-type dropdown can be
  // populated from the unit's ResMan pet policy (direct-unit mode only; holding
  // mode has no unit in context and falls back to a static list).
  const rentalDetailsStep = STEP_ORDER.indexOf("rentalDetails");
  const { data: unitData } = useGetUnitQuery(
    {
      unitID,
      moveInDate: moveInDateParam,
      term: leaseTermParam,
      propertyID: propertyId,
    },
    {
      skip:
        !unitID ||
        !propertyId ||
        (currentStep !== reviewStep && currentStep !== rentalDetailsStep),
    }
  ) as { data?: { rent?: number | string; petPolicy?: any[] } };

  // On mount: fetch the application and resume on the saved step
  useEffect(() => {
    if (!applicationId) {
      setHasHydrated(true);
      return;
    }
    getApplications({ id: applicationId, propertyId }).then((res) => {
      const list = (res as { data?: ApplicationRecord[] }).data;
      const record = list?.find((a) => a.id === applicationId);
      if (record?.role && record.role !== "Head of Household") {
        const leaseId = searchParams.get("leaseId") || "";
        const qs = new URLSearchParams({
          applicationId,
          propertyId,
          leaseId,
        }).toString();
        // Live-in aides have their own sub-flow; other non-head adults use the
        // co-applicant flow.
        const isAide = /live.?in.?aide/i.test(record.role || "");
        const dest = isAide
          ? "/application/affordable/live-in-aide"
          : "/application/affordable/co-applicant";
        navigate(`${dest}?${qs}`, {
          replace: true,
        });
        return;
      }
      const data = record?.applicationData || {};
      setApplicationData(data);
      const { completedSteps: completed, resumeStep } =
        computeResumePoint(data);
      setCompletedSteps(new Set(completed));
      setCurrentStep(resumeStep);
      if (record?.status === "submitted") setIsSubmitted(true);
      setIsResubmit(record?.status === "pending_resubmit");
      setHasHydrated(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, propertyId]);

  const handleStepSubmit = async (stepIndex: number, stepData: unknown) => {
    const stepKey = STEP_ORDER[stepIndex];
    const merged: ApplicationData = {
      ...applicationData,
      [stepKey]: stepData,
      language: i18n.language,
    };
    const res = (await saveApplicationData({
      id: applicationId,
      body: { applicationData: merged },
    })) as { data?: ApplicationRecord };
    if (res.data) {
      setApplicationData(res.data.applicationData);
      setCompletedSteps((prev) => {
        const next = new Set(prev);
        next.add(stepIndex);
        return next;
      });
      if (stepIndex < steps.length - 1) {
        setCurrentStep(stepIndex + 1);
      }
    }
  };

  // Save a partial slice of a step without marking it complete or advancing
  // to the next step. Used by UploadCenter to persist the file↔slot mapping
  // on every upload/delete so applicants can resume on revisit.
  const handlePartialStepSave = async (
    stepIndex: number,
    stepData: unknown
  ) => {
    const stepKey = STEP_ORDER[stepIndex];
    const existing =
      (applicationData[stepKey] as Record<string, unknown>) || {};
    const merged: ApplicationData = {
      ...applicationData,
      [stepKey]: { ...existing, ...(stepData as Record<string, unknown>) },
      language: i18n.language,
    };
    const res = (await saveApplicationData({
      id: applicationId,
      body: { applicationData: merged },
    })) as { data?: ApplicationRecord };
    if (res.data) {
      setApplicationData(res.data.applicationData);
    }
  };

  const handleSubmitApplication = async (
    stepData: unknown,
    moveInOverride?: string
  ) => {
    const lastIdx = STEP_ORDER.length - 1;
    const merged: ApplicationData = {
      ...applicationData,
      [STEP_ORDER[lastIdx]]: stepData,
      language: i18n.language,
    };
    lastReviewPayloadRef.current = stepData;
    setSubmitError("");
    setMoveInTooSoon(null);
    const fallbackError = t("affordable.application.reviewSign.submitError");
    // Step 1: persist reviewSign step so the server reads the final
    // applicationData (including reviewSign) from DB when it maps to ResMan.
    const saveRes = (await saveApplicationData({
      id: applicationId,
      body: { applicationData: merged },
    })) as {
      data?: ApplicationRecord;
      error?: { data?: { message?: string } };
    };
    if (saveRes.error || !saveRes.data) {
      setSubmitError(saveRes.error?.data?.message ?? fallbackError);
      return;
    }

    // Step 2: trigger the ResMan submit. Server reads applicationData from
    // DB, maps it to LIHTC lease-application XML, creates lease, PDFs, etc.
    // Direct-unit mode: forward the term + move-in date the applicant selected
    // on the availability step so the lease uses them instead of falling back
    // to AFFORDABLE_DEFAULT_TERM. (Holding mode has no leaseTerm in the URL, so
    // these stay undefined and the server keeps its holding-unit defaults.)
    const moveInDate = moveInOverride ?? selectedMoveInDate;
    const submitRes = (await submitAffordable({
      id: applicationId,
      body: {
        term: leaseTermParam || undefined,
        moveInDate: moveInDate || undefined,
      },
    })) as {
      data?: {
        application?: ApplicationRecord;
        leaseId?: string;
        requiresPayment?: boolean;
      };
      error?: { data?: { message?: string } };
    };
    // Surface backend rejections (e.g. "Unit is not available for lease at this
    // time.") instead of silently falling off the end — mirrors the conventional
    // LeaseApplication submit handler.
    if (submitRes.error || !submitRes.data?.application) {
      const tooSoon = parseMoveInTooSoonError(submitRes.error);
      if (tooSoon) {
        setMoveInTooSoon(tooSoon);
        return;
      }
      setSubmitError(submitRes.error?.data?.message ?? fallbackError);
      return;
    }
    const updatedApp = submitRes.data.application;
    setApplicationData(updatedApp.applicationData);
    setCompletedSteps((prev) => {
      const next = new Set(prev);
      next.add(lastIdx);
      return next;
    });
    // Charge-first (pay-first) direct-unit: no lease yet — go to the pay-first
    // payment screen keyed by applicationId only (no leaseid). LeasePayment then
    // posts to /v1/payments/application-payment; the lease is created on success.
    if (submitRes.data?.requiresPayment) {
      navigate(`/application/payment?applicationId=${applicationId}`);
      return;
    }
    // Legacy direct-unit (pay-first flags off): lease already created at submit.
    const leaseId = submitRes.data?.leaseId;
    if (unitID && leaseId) {
      navigate(
        `/application/payment?leaseid=${leaseId}&applicationId=${applicationId}`
      );
      return;
    }
    setIsSubmitted(true);
  };

  if (!applicationId || !propertyId) {
    return (
      <Wrapper>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4">
            {t("affordable.application.notFound")}
          </Typography>
        </Box>
      </Wrapper>
    );
  }

  if (isFetching || !hasHydrated || isUserLoading) {
    return <ManualLoader />;
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <AuthorizationToAssist
            applicationId={applicationId}
            defaultValues={
              applicationData.authorizationToAssist as
                | AuthorizationToAssistData
                | undefined
            }
            onSubmit={(data) => handleStepSubmit(0, data)}
            isSaving={isSaving}
          />
        );
      case 1: {
        const saved = applicationData.memberProfiles as
          | { firstName?: string; middleName?: string; lastName?: string }
          | undefined;
        const person = userData?.user?.person as
          | {
              FirstName?: string;
              MiddleName?: string;
              LastName?: string;
            }
          | undefined;
        const seeded = {
          ...(saved || {}),
          firstName: saved?.firstName || person?.FirstName || "",
          middleName: saved?.middleName || person?.MiddleName || "",
          lastName: saved?.lastName || person?.LastName || "",
        };
        return (
          <MemberProfiles
            defaultValues={seeded as any}
            primaryApplicantEmail={userData?.user?.email}
            onSubmit={(data) => handleStepSubmit(1, data)}
            isSaving={isSaving}
          />
        );
      }
      case 2:
        return (
          <RentalDetails
            defaultValues={applicationData.rentalDetails as any}
            onSubmit={(data) => handleStepSubmit(2, data)}
            onBack={handleBack}
            isSaving={isSaving}
            petPolicy={unitData?.petPolicy}
          />
        );
      case 3:
        return (
          <StudentDetails
            defaultValues={applicationData.studentDetails as any}
            onSubmit={(data) => handleStepSubmit(3, data)}
            onBack={handleBack}
            isSaving={isSaving}
            propertyContact={propertyContact}
          />
        );
      case 4:
        return (
          <SpecialNeeds
            defaultValues={applicationData.specialNeeds as any}
            onSubmit={(data) => handleStepSubmit(4, data)}
            onBack={handleBack}
            isSaving={isSaving}
          />
        );
      case 5:
        return (
          <SpecialStatus
            defaultValues={applicationData.specialStatus as any}
            onSubmit={(data) => handleStepSubmit(5, data)}
            onBack={handleBack}
            isSaving={isSaving}
            includeAnticipatedAddition
          />
        );
      case 6: {
        const profiles = applicationData.memberProfiles as
          | {
              members?: Array<{
                firstName?: string;
                lastName?: string;
                relationship?: string;
              }>;
            }
          | undefined;
        const minors = (profiles?.members ?? [])
          .filter(
            (m) =>
              !!m &&
              !!m.firstName &&
              !!m.lastName &&
              /child|minor/i.test(m.relationship ?? "")
          )
          .map((m) => ({
            id: `${m.firstName} ${m.lastName}`.trim(),
            firstName: m.firstName!,
            lastName: m.lastName!,
          }));
        return (
          <HouseholdIncome
            defaultValues={applicationData.householdIncome as any}
            onSubmit={(data) => handleStepSubmit(6, data)}
            onBack={handleBack}
            isSaving={isSaving}
            householdMinors={minors}
          />
        );
      }
      case 7:
        return (
          <Assets
            defaultValues={applicationData.assets as any}
            onSubmit={(data) => handleStepSubmit(7, data)}
            onBack={handleBack}
            isSaving={isSaving}
          />
        );
      case 8:
        return (
          <FinancialSummary
            applicationData={applicationData}
            onSubmit={(data) => handleStepSubmit(8, data)}
            onBack={handleBack}
            isSaving={isSaving}
          />
        );
      case 9:
        return (
          <UploadCenter
            applicationId={applicationId}
            applicationData={applicationData}
            onSubmit={(data) => handleStepSubmit(9, data)}
            onPartialSave={(data) => handlePartialStepSave(9, data)}
            onBack={handleBack}
            isSaving={isSaving}
            documentUploadOptional={
              propertyContact?.affordableDocumentUploadOptional ?? false
            }
          />
        );
      case 10:
        return (
          <ReviewSign
            applicationData={applicationData}
            unitNumber={unitID}
            rent={unitData?.rent}
            moveInDate={selectedMoveInDate}
            leaseTerm={leaseTermParam}
            onSubmit={handleSubmitApplication}
            onBack={handleBack}
            isSaving={isSubmitting}
            errorMessage={submitError}
            onDismissError={() => setSubmitError("")}
            correctionSlot={
              moveInTooSoon ? (
                <MoveInDateCorrection
                  applicationId={applicationId}
                  earliestMoveInDate={moveInTooSoon.earliestMoveInDate}
                  message={moveInTooSoon.message}
                  onSaved={async (isoDate) => {
                    setSelectedMoveInDate(isoDate);
                    setMoveInTooSoon(null);
                    if (lastReviewPayloadRef.current) {
                      await handleSubmitApplication(
                        lastReviewPayloadRef.current,
                        isoDate
                      );
                    }
                  }}
                />
              ) : undefined
            }
          />
        );
      default:
        return null;
    }
  };

  if (isSubmitted) {
    // Correction mode has no Auth0 session (the editor rode the correction token),
    // so the applicant's "Back to Applications" route would bounce staff to the
    // resident login. Show a terminal "close this tab" screen instead.
    const isCorrection = !!securityOptions.getCorrectionToken();
    return (
      <WizardShell>
        <RegisterReviewContainer>
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CheckCircleOutlineIcon
              color="success"
              sx={{ fontSize: 72, mb: 2 }}
            />
            <Typography variant="h3" sx={{ mb: 1 }}>
              {t(
                isCorrection
                  ? "affordable.application.reviewSign.success.correctionTitle"
                  : "affordable.application.reviewSign.success.title"
              )}
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, maxWidth: 480, mx: "auto" }}
            >
              {t(
                isCorrection
                  ? "affordable.application.reviewSign.success.correctionMessage"
                  : "affordable.application.reviewSign.success.message"
              )}
            </Typography>
            {isCorrection ? (
              <Button variant="contained" onClick={() => window.close()}>
                {t("affordable.application.reviewSign.success.close")}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => navigate("/settings/applications")}
              >
                {t(
                  "affordable.application.reviewSign.success.backToApplications"
                )}
              </Button>
            )}
          </Box>
        </RegisterReviewContainer>
      </WizardShell>
    );
  }

  return (
    <WizardShell
      stepper={
        <StepNavigation
          currentStep={currentStep}
          totalSteps={steps.length}
          onStepClick={handleStepClick}
          stepTitles={steps}
          completedSteps={completedSteps}
        />
      }
    >
      <RegisterReviewContainer>
        {isResubmit && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              border: "1px solid #F0C36D",
              backgroundColor: "#FFF8E6",
            }}
          >
            <Typography variant="body2" sx={{ color: "#7A5B00" }}>
              {t("affordable.application.correctionBanner")}
            </Typography>
          </Box>
        )}
        {renderStep()}
      </RegisterReviewContainer>
    </WizardShell>
  );
};
